import React, { useMemo, useState } from 'react';
import * as FaIcons from 'react-icons/fa';
import * as LuIcons from 'react-icons/lu';
import IconPickerField from './IconPickerField';
import MediaPickerField from './MediaPickerField';

const socialIconOptions = [
  'FaTwitter',
  'FaLinkedin',
  'FaGithub',
  'FaInstagram',
  'FaFacebook',
  'FaYoutube',
  'FaTiktok',
  'FaWhatsapp',
  'FaTelegram',
  'FaDiscord',
];

const emptySocialLink = () => ({
  label: '',
  url: '',
  iconType: 'icon',
  icon: 'FaTwitter',
});

const emptyQuickLink = () => ({
  label: '',
  url: '',
});

function resolveIconComponent(iconName) {
  if (!iconName) return null;
  return FaIcons[iconName] || LuIcons[iconName] || null;
}

export function FooterSocialLinksField({ value = [], onChange }) {
  const items = Array.isArray(value) && value.length > 0 ? value : [emptySocialLink()];

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptySocialLink()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Social Media Links
      </label>
      <p className="text-xs text-slate-400 dark:text-gray-500">
        Add, edit, or remove social links. Use an icon or pick an image from media library.
      </p>

      {items.map((item, index) => {
        const IconComponent = item.iconType === 'icon' ? resolveIconComponent(item.icon) : null;

        return (
          <div
            key={index}
            className="rounded-xl border border-slate-200 p-4 dark:border-white/10"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                Link {index + 1}
              </p>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-xs font-bold text-red-500 hover:text-red-400"
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  Label
                </label>
                <input
                  type="text"
                  value={item.label || ''}
                  onChange={(e) => updateItem(index, 'label', e.target.value)}
                  placeholder="Twitter"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                  URL
                </label>
                <input
                  type="text"
                  value={item.url || ''}
                  onChange={(e) => updateItem(index, 'url', e.target.value)}
                  placeholder="https://..."
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
                Icon Type
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'icon', label: 'Icon' },
                  { id: 'image', label: 'Media Image' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => updateItem(index, 'iconType', option.id)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                      item.iconType === option.id
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-slate-200 text-slate-500 dark:border-white/10 dark:text-gray-400'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3">
              {item.iconType === 'image' ? (
                <MediaPickerField
                  label="Social Icon Image"
                  value={item.icon || ''}
                  onChange={(url) => updateItem(index, 'icon', url)}
                />
              ) : (
                <div>
                  <IconPickerField
                    label="Social Icon"
                    value={item.icon || ''}
                    onChange={(icon) => updateItem(index, 'icon', icon)}
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {socialIconOptions.map((iconName) => {
                      const SuggestedIcon = resolveIconComponent(iconName);
                      const isActive = item.icon === iconName;
                      return (
                        <button
                          key={iconName}
                          type="button"
                          onClick={() => updateItem(index, 'icon', iconName)}
                          title={iconName}
                          className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                            isActive
                              ? 'border-primary bg-primary/15 text-primary'
                              : 'border-slate-200 text-slate-500 hover:border-primary/40 hover:text-primary dark:border-white/10 dark:text-gray-400'
                          }`}
                        >
                          {SuggestedIcon ? <SuggestedIcon size={16} /> : null}
                        </button>
                      );
                    })}
                  </div>
                  {IconComponent ? (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-[#101010] dark:text-gray-300">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <IconComponent size={16} />
                      </span>
                      Preview: {item.icon}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add Social Link
      </button>
    </div>
  );
}

export function FooterQuickLinksField({ value = [], onChange }) {
  const items = Array.isArray(value) && value.length > 0 ? value : [emptyQuickLink()];

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptyQuickLink()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [emptyQuickLink()]);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Quick Links
      </label>
      <p className="text-xs text-slate-400 dark:text-gray-500">
        Manage footer navigation links shown in the Quick Links column.
      </p>

      {items.map((item, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 p-3 md:grid-cols-[1fr_1fr_auto] dark:border-white/10"
        >
          <input
            type="text"
            value={item.label || ''}
            onChange={(e) => updateItem(index, 'label', e.target.value)}
            placeholder="Link label"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <input
            type="text"
            value={item.url || ''}
            onChange={(e) => updateItem(index, 'url', e.target.value)}
            placeholder="#section or https://..."
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <button
            type="button"
            onClick={() => removeItem(index)}
            className="rounded-lg border border-red-500/30 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-500/10"
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add Quick Link
      </button>
    </div>
  );
}

export function FooterIconPreview({ link }) {
  const IconComponent = useMemo(() => {
    if (!link || link.iconType === 'image') return null;
    return resolveIconComponent(link.icon);
  }, [link]);

  if (!link?.url && !link?.icon && !link?.label) return null;

  if (link.iconType === 'image' && link.icon) {
    return (
      <span className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-black/10">
        <img src={link.icon} alt={link.label || 'Social'} className="h-5 w-5 object-contain" />
      </span>
    );
  }

  if (IconComponent) {
    return (
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-black">
        <IconComponent size={16} />
      </span>
    );
  }

  return (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-xs font-bold text-black">
      {(link.label || '?').slice(0, 1)}
    </span>
  );
}
